import React, { useEffect, useState } from 'react';
import { Laptop, Folder, FileText, FileDown, Video, File, Puzzle, X, GripVertical, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';
import { getLibraryItems, getAllLibraryFolders } from '../../../../services/libraryService';
import { getCoachByPlayerProfile } from '../../../../services/firestoreService';
import { ACCESS_LEVEL_CONFIG, normalizeAccessLevel } from '../../../../constants/accessLevels';

const TYPE_ICONS = {
  folder: Folder,
  pgn: FileText,
  pdf: FileDown,
  video: Video,
  file: File,
  puzzle: Puzzle,
};

const MAX_ITEMS = 4;

const LibraryShowcaseEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const updateContent = (field, value) => {
    onChange({ [field]: value });
  };

  // Load all library items (root + folders) for the dropdown
  useEffect(() => {
    if (!profileId) return;

    const load = async () => {
      try {
        const coach = await getCoachByPlayerProfile(profileId);
        if (!coach?.id) {
          setLoading(false);
          return;
        }

        const [rootItems, folders] = await Promise.all([
          getLibraryItems(coach.id, null),
          getAllLibraryFolders(coach.id),
        ]);

        // Combine root-level items with all folders (deduplicated)
        const folderIds = new Set(folders.map((f) => f.id));
        const combined = [
          ...folders,
          ...rootItems.filter((item) => !folderIds.has(item.id)),
        ];
        combined.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        setAllItems(combined);
      } catch (err) {
        console.error('Error loading library items for editor:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [profileId]);

  const rawSelectedIds = content.selectedItems || [];

  // Auto-clean orphaned IDs that no longer exist in the library
  const selectedIds = rawSelectedIds.filter((id) => allItems.some((item) => item.id === id));
  if (!loading && selectedIds.length !== rawSelectedIds.length) {
    updateContent('selectedItems', selectedIds);
  }

  const addItem = (itemId) => {
    if (selectedIds.length >= MAX_ITEMS || selectedIds.includes(itemId)) return;
    updateContent('selectedItems', [...selectedIds, itemId]);
  };

  const removeItem = (itemId) => {
    updateContent('selectedItems', selectedIds.filter((id) => id !== itemId));
  };

  const moveItem = (index, direction) => {
    const newItems = [...selectedIds];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    updateContent('selectedItems', newItems);
  };

  const availableItems = allItems.filter((item) => !selectedIds.includes(item.id));
  const selectedItemObjects = selectedIds
    .map((id) => allItems.find((item) => item.id === id))
    .filter(Boolean);

  return (
    <div className="library-showcase-editor">
      <div className="editor-section">
        <div className="editor-header">
          <Laptop size={20} />
          <h3>Library Showcase</h3>
        </div>
        <p className="editor-desc">
          Hand-pick up to {MAX_ITEMS} items from your library to showcase in a MacBook-themed display.
        </p>
      </div>

      <div className="editor-section">
        <label className="editor-label">Section Title</label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => updateContent('title', e.target.value)}
          placeholder="Study Library"
          className="editor-input"
        />
      </div>

      <div className="editor-section">
        <label className="editor-label">Description</label>
        <textarea
          value={content.description || ''}
          onChange={(e) => updateContent('description', e.target.value)}
          placeholder="Optional description"
          rows={2}
          className="editor-textarea"
        />
      </div>

      <div className="editor-section">
        <label className="editor-label">
          Selected Items ({selectedIds.length}/{MAX_ITEMS})
        </label>

        {selectedItemObjects.length > 0 && (
          <div className="selected-chips">
            {selectedItemObjects.map((item, index) => {
              const Icon = TYPE_ICONS[item.type] || File;
              const accessLevel = normalizeAccessLevel(item.accessLevel);
              const accessConfig = ACCESS_LEVEL_CONFIG[accessLevel];
              return (
                <div key={item.id} className="selected-chip">
                  <button
                    className="chip-move"
                    onClick={() => moveItem(index, -1)}
                    disabled={index === 0}
                    title="Move up"
                  >
                    <GripVertical size={12} />
                  </button>
                  <div className={`chip-icon ${item.type}`}>
                    <Icon size={12} />
                  </div>
                  <span className="chip-title">{item.title}</span>
                  <span
                    className="chip-access"
                    style={{ color: accessConfig?.color }}
                  >
                    {accessConfig?.label}
                  </span>
                  <button
                    className="chip-remove"
                    onClick={() => removeItem(item.id)}
                    title="Remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {selectedIds.length < MAX_ITEMS && (
          <div className="item-picker">
            {loading ? (
              <p className="dropdown-loading">Loading library items...</p>
            ) : availableItems.length === 0 ? (
              <p className="dropdown-empty">No more items available</p>
            ) : (
              <>
                <button
                  type="button"
                  className="picker-toggle"
                  onClick={() => setPickerOpen(!pickerOpen)}
                >
                  <Plus size={14} />
                  <span>Add an item...</span>
                  {pickerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {pickerOpen && (
                  <div className="picker-list">
                    {availableItems.map((item) => {
                      const Icon = TYPE_ICONS[item.type] || File;
                      const accessLevel = normalizeAccessLevel(item.accessLevel);
                      const accessConfig = ACCESS_LEVEL_CONFIG[accessLevel];
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className="picker-item"
                          onClick={() => {
                            addItem(item.id);
                            if (selectedIds.length + 1 >= MAX_ITEMS) setPickerOpen(false);
                          }}
                        >
                          <div className={`chip-icon ${item.type}`}>
                            <Icon size={12} />
                          </div>
                          <span className="picker-item-title">{item.title}</span>
                          <span
                            className="chip-access"
                            style={{ color: accessConfig?.color }}
                          >
                            {accessConfig?.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="editor-section">
        <label className="editor-label">Button Text</label>
        <input
          type="text"
          value={content.buttonText || ''}
          onChange={(e) => updateContent('buttonText', e.target.value)}
          placeholder="Browse Full Library"
          className="editor-input"
        />
      </div>

      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            block={block}
            onChange={onChange}
            features={BLOCK_FEATURES['library-showcase']}
          />
          <ColorsFontsPanel
            block={block}
            onChange={onChange}
            theme={theme}
            features={BLOCK_FEATURES['library-showcase']}
          />
        </>
      )}

      <style>{`
        .library-showcase-editor {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .editor-header {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--editor-text, #e2e8f0);
        }

        .editor-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .editor-desc {
          margin: 8px 0 0;
          font-size: 0.85rem;
          color: var(--editor-muted, #94a3b8);
          line-height: 1.4;
        }

        .editor-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .editor-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--editor-text, #e2e8f0);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .editor-input,
        .editor-textarea {
          padding: 8px 12px;
          background: var(--editor-input-bg, rgba(255, 255, 255, 0.05));
          border: 1px solid var(--editor-border, rgba(255, 255, 255, 0.1));
          border-radius: 6px;
          color: var(--editor-text, #e2e8f0);
          font-size: 0.9rem;
          font-family: inherit;
        }

        .editor-input:focus,
        .editor-textarea:focus {
          outline: none;
          border-color: #749555;
        }

        .editor-textarea {
          resize: vertical;
        }

        .selected-chips {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .selected-chip {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
        }

        .chip-move {
          background: none;
          border: none;
          color: var(--editor-muted, #94a3b8);
          cursor: grab;
          padding: 2px;
          display: flex;
          align-items: center;
        }

        .chip-move:disabled {
          opacity: 0.3;
          cursor: default;
        }

        .chip-icon {
          width: 22px;
          height: 22px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: white;
        }

        .chip-icon.folder { background: #3b82f6; }
        .chip-icon.pgn { background: #749555; }
        .chip-icon.pdf { background: #ef4444; }
        .chip-icon.video { background: #8b5cf6; }
        .chip-icon.file { background: #6b7280; }
        .chip-icon.puzzle { background: #f59e0b; }

        .chip-title {
          flex: 1;
          font-size: 0.85rem;
          color: var(--editor-text, #e2e8f0);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chip-access {
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          flex-shrink: 0;
        }

        .chip-remove {
          background: none;
          border: none;
          color: #ef4444;
          cursor: pointer;
          padding: 2px;
          display: flex;
          align-items: center;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .chip-remove:hover {
          opacity: 1;
        }

        .dropdown-loading,
        .dropdown-empty {
          font-size: 0.8rem;
          color: var(--editor-muted, #94a3b8);
          margin: 4px 0;
        }

        .picker-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 12px;
          background: var(--editor-input-bg, rgba(255, 255, 255, 0.05));
          border: 1px solid var(--editor-border, rgba(255, 255, 255, 0.1));
          border-radius: 6px;
          color: var(--editor-muted, #94a3b8);
          font-size: 0.85rem;
          font-family: inherit;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        .picker-toggle:hover {
          border-color: #749555;
        }

        .picker-toggle span {
          flex: 1;
          text-align: left;
        }

        .picker-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
          max-height: 200px;
          overflow-y: auto;
          margin-top: 4px;
          padding: 4px;
          background: var(--editor-input-bg, rgba(255, 255, 255, 0.05));
          border: 1px solid var(--editor-border, rgba(255, 255, 255, 0.1));
          border-radius: 6px;
        }

        .picker-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          background: none;
          border: none;
          border-radius: 4px;
          color: var(--editor-text, #e2e8f0);
          font-size: 0.82rem;
          font-family: inherit;
          cursor: pointer;
          transition: background 0.15s;
          text-align: left;
        }

        .picker-item:hover {
          background: rgba(116, 149, 85, 0.15);
        }

        .picker-item-title {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
    </div>
  );
};

export default LibraryShowcaseEditor;
